import { useEffect, useState } from 'react';
import { Plus, X, Coins } from 'lucide-react';
import { previewAmount } from '@/lib/currencies/preview-amount';
import { adminFetch } from '@/api';

interface Currency {
  code: string;
  symbol: string;
  name: string;
  nameHi: string | null;
  decimals: number;
  indianGrouping: boolean;
  isActive: boolean;
  isDefault: boolean;
  userCount: number;
  groupCount: number;
}

const EMPTY = { code: '', symbol: '', name: '', nameHi: '', decimals: 2, indianGrouping: false };

const inputCls =
  'w-full px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all';

export default function CurrenciesPage() {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCurrencies = async (): Promise<Currency[]> => {
    const res = await adminFetch('/api/currencies');
    const data = await res.json();
    return data.currencies ?? [];
  };

  const load = async () => setCurrencies(await fetchCurrencies());

  useEffect(() => {
    let alive = true;
    fetchCurrencies().then((rows) => {
      if (alive) setCurrencies(rows);
    });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = async (c: Currency) => {
    setError('');
    const res = await adminFetch('/api/currencies', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: c.code, isActive: !c.isActive }),
    });
    if (!res.ok) setError((await res.json()).error || 'Failed to update');
    load();
  };

  const add = async () => {
    setError('');
    setSaving(true);
    const res = await adminFetch('/api/currencies', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (!res.ok) {
      setError((await res.json()).error || 'Failed to add');
      return;
    }
    setForm(EMPTY);
    setShowAdd(false);
    load();
  };

  const activeCount = currencies.filter((c) => c.isActive).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-white tracking-tight">Currencies</h1>
          <p className="text-zinc-500 text-[13px] mt-0.5">
            {activeCount} in the app · {currencies.length - activeCount} off. Turning one off hides it from new
            choices; groups already using it keep working.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 text-[13px] font-semibold transition-all"
        >
          <Plus className="w-4 h-4" />
          Add currency
        </button>
      </div>

      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-between">
          <p className="text-red-400 text-[12px] font-medium">{error}</p>
          <button onClick={() => setError('')} className="text-red-400/50 hover:text-red-400">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[720px] text-[13px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
              {['Currency', 'Code', 'Example', 'Decimals', 'Users', 'Groups', 'In app'].map((h) => (
                <th key={h} className="text-left font-semibold px-5 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currencies.map((c) => (
              <tr key={c.code} className="border-t border-white/[0.04]">
                <td className="px-5 py-3 text-white">
                  <span className="inline-grid place-items-center min-w-7 h-7 px-1 mr-2.5 rounded-lg bg-white/[0.05] font-bold text-[12px]">
                    {c.symbol.trim()}
                  </span>
                  {c.name}
                  {c.isDefault && (
                    <span className="ml-2 text-[10.5px] font-bold text-amber-400 bg-amber-400/10 rounded-full px-2 py-0.5">
                      Default
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-zinc-300 font-mono">{c.code}</td>
                <td className="px-5 py-3 text-zinc-300">{previewAmount(c, 125000)}</td>
                <td className="px-5 py-3 text-zinc-400">{c.decimals}</td>
                <td className="px-5 py-3 text-zinc-400">{c.userCount.toLocaleString('en-IN')}</td>
                <td className="px-5 py-3 text-zinc-400">{c.groupCount.toLocaleString('en-IN')}</td>
                <td className="px-5 py-3">
                  <button
                    role="switch"
                    aria-checked={c.isActive}
                    aria-label={`${c.code} in app`}
                    disabled={c.isDefault}
                    title={c.isDefault ? 'The default currency is always on' : undefined}
                    onClick={() => toggle(c)}
                    className={`relative w-9 h-5 rounded-full transition-colors disabled:opacity-50 ${
                      c.isActive ? 'bg-emerald-500' : 'bg-zinc-700'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                        c.isActive ? 'left-[18px]' : 'left-0.5'
                      }`}
                    />
                  </button>
                </td>
              </tr>
            ))}
            {currencies.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-zinc-500">
                  <Coins className="w-5 h-5 mx-auto mb-2 opacity-60" />
                  No currencies yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md bg-[#0b0b0d] border border-white/[0.08] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-semibold text-white">Add currency</p>
              <button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-zinc-300">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Code (ISO 4217)</span>
                <input className={inputCls} value={form.code} maxLength={3} placeholder="SGD"
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Symbol</span>
                <input className={inputCls} value={form.symbol} placeholder="S$"
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Name (English)</span>
                <input className={inputCls} value={form.name} placeholder="Singapore Dollar"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Name (Hindi)</span>
                <input className={inputCls} value={form.nameHi} placeholder="सिंगापुर डॉलर"
                  onChange={(e) => setForm({ ...form, nameHi: e.target.value })} />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Decimals</span>
                <select className={inputCls} value={form.decimals}
                  onChange={(e) => setForm({ ...form, decimals: Number(e.target.value) })}>
                  {[0, 1, 2, 3].map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-[11px] text-zinc-400">Number style</span>
                <select className={inputCls} value={form.indianGrouping ? 'in' : 'intl'}
                  onChange={(e) => setForm({ ...form, indianGrouping: e.target.value === 'in' })}>
                  <option value="intl">1,234,567 (international)</option>
                  <option value="in">12,34,567 (Indian)</option>
                </select>
              </label>
            </div>
            <div className="px-3 py-2.5 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20 text-[12px] text-emerald-200">
              Preview in the app: <b>{previewAmount({ ...form, symbol: form.symbol || '' })}</b>
            </div>
            <p className="text-[11px] text-zinc-500">
              Put a trailing space in the symbol if it is letters (e.g. &quot;AED &quot;).
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)}
                className="px-4 py-2 rounded-lg text-zinc-400 text-[12px] font-medium hover:text-zinc-200">
                Cancel
              </button>
              <button onClick={add} disabled={saving || !form.code || !form.symbol.trim() || !form.name.trim()}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-black text-[12px] font-semibold hover:bg-emerald-400 disabled:opacity-40">
                Add &amp; turn on
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
